import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Head } from '@inertiajs/react';
import { DeckGL } from '@deck.gl/react';
import { OrthographicView, OrthographicViewport, LinearInterpolator } from '@deck.gl/core';
import type { OrthographicViewState, PickingInfo } from '@deck.gl/core';
import {
  ScatterplotLayer,
  TextLayer,
  LineLayer,
  PolygonLayer,
} from '@deck.gl/layers';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { OrdinalIcon } from '@/components/OrdinalIcon';
import {
  getAtlas,
  searchAtlas,
  type AtlasConnection,
  type AtlasData,
  type AtlasDistrict,
  type AtlasDocument,
  type AtlasGap,
  type AtlasMunicipality,
  type AtlasRelationship,
  type AtlasSearchResults,
} from '@/api/knowledgeAtlas';
import { IntelligencePanel } from '@/components/knowledge_atlas/IntelligencePanel';
import { AtlasSearch } from '@/components/knowledge_atlas/AtlasSearch';
import {
  HoverWhyCard,
  type HoverConnection,
} from '@/components/knowledge_atlas/HoverWhyCard';
import {
  ConnectionsLegend,
  type LegendConnection,
} from '@/components/knowledge_atlas/ConnectionsLegend';
import { DocumentCards } from '@/components/knowledge_atlas/DocumentCards';
import { CoveragePanel } from '@/components/knowledge_atlas/CoveragePanel';
import {
  clamp,
  darken,
  hexToRgb,
  lodFromZoom,
  muted,
  shortenCoreLabel,
  smoothstep,
  type RGBA,
} from '@/components/knowledge_atlas/atlasHelpers';

interface KnowledgeAtlasProps {
  municipalities: AtlasMunicipality[];
}

// ===========================================================================
// TUNABLE VISUAL CONSTANTS
// ---------------------------------------------------------------------------
// The Atlas reads as a MUNICIPAL PLANNING ARTIFACT on a LIGHT canvas: GIS /
// zoning map / blueprint / Figma / Linear. Crisp parcel edges, pale zoning
// tints, a faint graph-paper grid, a civic downtown core. NO space, NO glow,
// NO additive blending. A human will fine-tune afterward; everything that
// controls palette / alpha / size / zoom thresholds lives here so the renderer
// below reads as "what", not "what value". Colors are [r,g,b] 0-255; alphas
// are 0-255 unless noted as 0-1.
// ===========================================================================
const VIS = {
  // --- Light planning canvas (CSS behind a transparent deck canvas) ---
  canvasBase: '#F4F6F8', // near-white cool grey paper
  // Blueprint / Figma graph-paper grid: a fine 1px grid every ~40px and a
  // slightly stronger line every ~200px. Subtle, crisp, never busy.
  gridFine: 'rgba(43, 56, 72, 0.05)', // hairline cells (~40px)
  gridFineStep: 40,
  gridMajor: 'rgba(43, 56, 72, 0.09)', // stronger rules (~200px)
  gridMajorStep: 200,

  // --- Districts = planning zones (crisp, not nebulae) ---
  zoneFillAlpha: 0.16, // 0-1; pale zoning tint over the paper
  zoneBorderAlpha: 235, // 0-255; crisp parcel edge
  zoneBorderWidthPx: 1.75,
  zoneBorderDarken: 0.18, // border slightly deeper than the fill hue
  // Soft Figma/Linear elevation: a slightly offset + larger polygon underneath
  // at very low dark alpha, just enough to lift the zone off the paper.
  zoneShadowAlpha: 0.045, // 0-1, dark
  zoneShadowScale: 1.012, // shadow polygon scaled out from centroid
  zoneShadowOffset: [6, 8] as [number, number], // world-unit down-right offset

  // --- Roads = connector network (streets / GIS links) ---
  connectorColor: [88, 104, 124] as [number, number, number], // muted slate blue-grey
  connectorAlphaDefault: 90, // 0-255 (~0.35) quiet but readable
  connectorAlphaLit: 215, // darkened/emphasized on focus
  connectorAlphaDimmed: 22, // unrelated connectors recede on focus
  connectorWidthDefault: 0.7,
  connectorWidthLit: 1.8,

  // --- Landmarks = documents (small crisp map nodes) ---
  landmarkBaseRadius: 2.2,
  landmarkDegreeScale: 1.1, // sqrt(degree) * this; kept small + tidy
  landmarkFill: [110, 124, 142] as [number, number, number], // muted slate, reads on light
  landmarkLit: [55, 71, 92] as [number, number, number], // darker when in focus
  landmarkSearchHit: [154, 123, 63] as [number, number, number], // civic gold
  landmarkBorderDarken: 0.4, // thin darker border around each node
  landmarkBorderWidthPx: 0.75,
  landmarkFillAlpha: 235, // 0-255
  landmarkDimAlpha: 70, // 0-255 when not in the focused neighborhood

  // --- Downtown core = the capital (Municipal Code) ---
  coreAccent: [42, 52, 65] as [number, number, number], // deep civic slate/navy
  coreRing: [42, 52, 65] as [number, number, number], // concentric city-center ring
  coreMarkerRadiusMul: 2.6, // vs a normal landmark of same degree
  coreRingRadiusMul: 4.2, // the ring sits outside the marker
  coreRingWidthPx: 1.5,
  coreRingAlpha: 150, // 0-255
  coreBorderColor: [255, 255, 255] as [number, number, number], // crisp white keyline
  coreBorderWidthPx: 1.25,
  coreLabelColor: [33, 41, 51] as [number, number, number], // dark slate text

  // --- Typography (Linear/Figma crispness) ---
  districtLabelSize: 13,
  districtCountSize: 10,
  districtLabelColor: [43, 48, 56] as [number, number, number], // confident dark slate
  districtCountColor: [110, 122, 138] as [number, number, number], // quieter grey
  districtLabelBg: [244, 246, 248] as [number, number, number], // paper, for legibility
  coreLabelSize: 14,
  landmarkLabelSize: 11,
  landmarkLabelColor: [71, 84, 100] as [number, number, number],
  labelPaperOutline: [244, 246, 248] as [number, number, number], // halo so text reads on lines

  // --- Document info cards (semantic-zoom LOD: dots -> Figma/Linear cards) ---
  // At close zoom the landmark dots cross-fade into elegant light info cards
  // (HTML overlays positioned via deck.project()). They are CULLED to the
  // viewport and CAPPED to the top-N by degree so a dense corpus never floods
  // the canvas or hurts perf. The LOD opacity ramp lives in atlasHelpers'
  // `lodFromZoom().cards` (a higher band than landmarkLabels); the dot layers
  // fade OUT across the same band so the swap reads as one smooth cross-fade.
  card: {
    maxCards: 28, // hard cap on simultaneously rendered cards (top-N by degree)
    width: 188, // px, a normal landmark card
    coreWidth: 212, // px, the downtown-core card reads a touch larger/prouder
    cullMarginPx: 160, // keep cards just off the edge so they enter smoothly
    // Across this zoom band the DOTS fade out as the CARDS fade in (cross-fade).
    // Kept in sync with lodFromZoom().cards (smoothstep 0.8 -> 1.6).
    dotFadeOutStart: 0.8,
    dotFadeOutEnd: 1.6,
  },

  // --- On-map cross-district edge theme labels (the map narrates itself) ---
  // A small, subtle text label at the midpoint of the most significant
  // cross-district edges, showing the shared theme (e.g. "detention"). Only the
  // top few; LOD-gated so they fade with zoom and never clutter.
  edgeLabelMax: 5, // how many edges get a midpoint theme label, max
  edgeLabelSize: 10, // px
  edgeLabelColor: [96, 109, 126] as [number, number, number], // quiet slate
  edgeLabelAlpha: 200, // 0-255 at full LOD strength
  edgeLabelBg: [244, 246, 248] as [number, number, number], // paper chip behind text
  edgeLabelBgAlpha: 165, // 0-255 at full LOD strength

  // --- "Ask the atlas" search emphasis (matched set lit, rest dimmed) ---
  // On a search, matched LANDMARKS take a civic-gold emphasis (reusing
  // landmarkSearchHit) and matched DISTRICT ZONES get a subtle gold tint over
  // their normal zoning fill; everything NOT in the matched set is dimmed so the
  // answer set reads instantly. Restrained, planning-styled.
  search: {
    matchedDistrictTint: [154, 123, 63] as [number, number, number], // civic gold
    matchedDistrictFillAlpha: 0.3, // 0-1; gold tint over the zone fill
    matchedDistrictBorderAlpha: 245, // 0-255; crisp gold parcel edge
    matchedDistrictBorderWidthPx: 2.25,
    dimZoneFillMul: 0.35, // unmatched zones recede (multiplies their fill alpha)
    dimZoneBorderMul: 0.4, // ...and their borders
    fitPadding: 2.0, // bounds padding when framing the matched set
    fitMaxZoom: 0.9, // never punch in past this when framing matches
  },

  // --- Knowledge-gap flags (subtle, on-aesthetic, never alarming) ---
  // Isolated documents get a small HOLLOW / DASHED muted ring so disconnected
  // knowledge is spottable at a glance; thin districts get a faint hatched
  // overlay. Muted slate, restrained alphas — informative, not a warning klaxon.
  gap: {
    isolatedRingColor: [150, 138, 110] as [number, number, number], // muted clay/taupe
    isolatedRingRadiusMul: 2.6, // ring sits outside the (small) isolated node
    isolatedRingWidthPx: 1.25,
    isolatedRingAlpha: 165, // 0-255
    isolatedDashLen: 4, // common-unit dash on the ring (reads as "open")
    isolatedDashGap: 3,
    thinHatchColor: [120, 112, 96] as [number, number, number], // muted warm grey
    thinHatchAlpha: 0.12, // 0-1; faint diagonal hatch tint over a thin district
  },

  font: 'Inter, system-ui, sans-serif',
} as const;

// ===========================================================================
// ZOOM / CAMERA CONTROLLER TUNABLES
// ---------------------------------------------------------------------------
// The OrthographicView controller. Earlier the zoom felt "slow then snappy":
// the scroll speed was tiny (crawl at first) and the usable zoom RANGE was
// narrow, so each notch covered a large fraction of the range near the close
// end -> sluggish far out, sudden up close. We widen the range and raise +
// smooth the scroll speed so a normal wheel/trackpad gesture zooms at a
// comfortable, consistent rate, centered on the cursor. Human-tunable.
// ===========================================================================
const ZOOM = {
  scrollSpeed: 0.058, // per wheel-tick zoom delta (0.08 -> 0.068 -> 0.058, two 15% steps)
  // IMPORTANT: smooth MUST be false. deck's smooth scroll animates each tick as a
  // transition, but our viewState is a CONTROLLED component, so onViewStateChange
  // resets the in-flight transition every tick -> it barely moves ("super slow"),
  // then applies the accumulated delta when you stop ("suddenly fast"). smooth:false
  // makes each wheel notch apply immediately and proportionally (cursor-centered by deck).
  scrollSmooth: false,
  inertiaMs: 0, // no glide; immediate, predictable wheel zoom
  // Widened, retuned range. The old [-4, 3] paired with a slow speed made the
  // far end feel like nothing happened; pulling minZoom in a touch and keeping a
  // generous maxZoom makes the whole sweep feel even / linear-ish to the user.
  minZoom: -5,
  maxZoom: 4,
} as const;

const VIEW_ID = 'atlas';
const CAMERA_TRANSITION = {
  // Calm + quick: restrained ease, no cinematic drama.
  transitionDuration: 850,
  transitionInterpolator: new LinearInterpolator(['target', 'zoom']),
  transitionEasing: (t: number) => 1 - Math.pow(1 - t, 3), // ease-out cubic
};

const INITIAL_VIEW_STATE: OrthographicViewState = {
  target: [0, 0, 0],
  zoom: -1.2,
  minZoom: ZOOM.minZoom,
  maxZoom: ZOOM.maxZoom,
};

export default function KnowledgeAtlas({ municipalities }: KnowledgeAtlasProps) {
  // Default to the READY municipality with the most documents. Only warmed cities are selectable
  // (an unwarmed one would 503 on a slow inline build), so the default must be one too; null when
  // nothing has been warmed yet (the scheduled warm pass fills these in shortly after).
  const defaultMunicipalityId = useMemo(() => {
    const ready = municipalities.filter((m) => m.ready);
    if (ready.length === 0) return null;
    return [...ready].sort((a, b) => b.documentCount - a.documentCount)[0].id;
  }, [municipalities]);

  const [municipalityId, setMunicipalityId] = useState<number | null>(defaultMunicipalityId);
  const [data, setData] = useState<AtlasData | null>(null);
  const [loading, setLoading] = useState(false);
  // True while the server is warming this city's atlas in the background (data endpoint returned
  // 202 and we're polling). Drives a distinct "building" loading message. Rare in practice (only a
  // cold/evicted cache), since the picker only offers already-warmed cities.
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [viewState, setViewState] = useState<OrthographicViewState>(INITIAL_VIEW_STATE);
  const zoom = (viewState.zoom as number) ?? -1.2;

  // Interaction state.
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [highlightedIds, setHighlightedIds] = useState<Set<number>>(new Set());
  // Cursor position (viewport px) for the floating "why" hover card.
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
  // The district pair to spotlight (hover a legend row, or a focused doc's
  // cross-district links). { sourceDistrictId, targetDistrictId } or null.
  const [hoveredPair, setHoveredPair] = useState<{
    sourceDistrictId: number;
    targetDistrictId: number;
  } | null>(null);
  // A hovered district (from the zone polygon): spotlights its inter-district
  // links and surfaces its bridge themes in a small floating chip.
  const [hoveredDistrictId, setHoveredDistrictId] = useState<number | null>(null);

  // Search ("ask the atlas").
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<AtlasSearchResults | null>(null);
  // Matched DISTRICT ids from the last search, so matched zones take the gold
  // emphasis and the rest dim. Document hits live in `highlightedIds`.
  const [matchedDistrictIds, setMatchedDistrictIds] = useState<Set<number>>(new Set());
  // True once a search has run: drives the dim-the-rest treatment + camera fit.
  const isSearchActive = searchResults !== null;

  // Load fade (0 -> 1 over ~600ms after data arrives): a calm settle, not a bloom.
  const [reveal, setReveal] = useState(0);
  const revealRaf = useRef<number | null>(null);

  // The canvas wrapper, so we can convert deck canvas-relative hover coords into
  // viewport coords for the fixed-positioned hover "why" card.
  const canvasRef = useRef<HTMLDivElement>(null);
  // Canvas pixel size, tracked so we can build an OrthographicViewport to
  // project() world coords -> canvas px for the HTML document cards.
  const [canvasSize, setCanvasSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const update = () =>
      setCanvasSize({ width: el.clientWidth, height: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // --------------------------------------------------------------------------
  // Data loading
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (municipalityId === null) return;
    let cancelled = false;
    const controller = new AbortController();

    setLoading(true);
    setBuilding(false);
    setError(null);
    setData(null);
    setSelectedId(null);
    setHoveredId(null);
    setHoveredPair(null);
    setHighlightedIds(new Set());
    setSearchResults(null);
    setMatchedDistrictIds(new Set());
    setReveal(0);

    getAtlas(municipalityId, {
      signal: controller.signal,
      onBuilding: () => {
        if (!cancelled) setBuilding(true);
      },
    })
      .then((result) => {
        if (cancelled) return;
        setData(result);
        fitToContent(result, setViewState);
        startReveal();
      })
      .catch((err: unknown) => {
        // Aborts are expected when switching municipalities / unmounting — not an error.
        if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) return;
        setError(err instanceof Error ? err.message : 'Failed to load the atlas');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setBuilding(false);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (revealRaf.current) cancelAnimationFrame(revealRaf.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipalityId]);

  const startReveal = useCallback(() => {
    if (revealRaf.current) cancelAnimationFrame(revealRaf.current);
    const start = performance.now();
    const duration = 600;
    const tick = (now: number) => {
      const t = clamp((now - start) / duration, 0, 1);
      setReveal(1 - Math.pow(1 - t, 3));
      if (t < 1) revealRaf.current = requestAnimationFrame(tick);
    };
    revealRaf.current = requestAnimationFrame(tick);
  }, []);

  // --------------------------------------------------------------------------
  // Lookups + neighborhood computation
  // --------------------------------------------------------------------------
  const docById = useMemo(() => {
    const m = new Map<number, AtlasDocument>();
    data?.documents.forEach((d) => m.set(d.id, d));
    return m;
  }, [data]);

  // The downtown core document, if the backend named one and it exists.
  const capital = useMemo(() => {
    const id = data?.anchorDocumentId;
    if (id === null || id === undefined) return null;
    return docById.get(id) ?? null;
  }, [data, docById]);

  // For each document, the set of directly connected document ids.
  const neighbors = useMemo(() => {
    const m = new Map<number, Set<number>>();
    data?.relationships.forEach(({ source, target }) => {
      if (!m.has(source)) m.set(source, new Set());
      if (!m.has(target)) m.set(target, new Set());
      m.get(source)!.add(target);
      m.get(target)!.add(source);
    });
    return m;
  }, [data]);

  // Per-document adjacency carrying the relationship (so we can read its
  // sharedTerms and weight for the hover "why" card).
  const relsByDoc = useMemo(() => {
    const m = new Map<number, AtlasRelationship[]>();
    data?.relationships.forEach((r) => {
      if (!m.has(r.source)) m.set(r.source, []);
      if (!m.has(r.target)) m.set(r.target, []);
      m.get(r.source)!.push(r);
      m.get(r.target)!.push(r);
    });
    return m;
  }, [data]);

  // Districts looked up by id (for legend labels + bridge themes).
  const districtById = useMemo(() => {
    const m = new Map<number, AtlasDistrict>();
    data?.districts.forEach((d) => m.set(d.id, d));
    return m;
  }, [data]);

  // Cross-district connections. Prefer the backend `connections` payload; if
  // absent, derive the strongest cross-district links from relationships that
  // carry sharedTerms (graceful degrade), so the legend + edge labels still work.
  const connections = useMemo<AtlasConnection[]>(() => {
    if (!data) return [];
    if (data.connections && data.connections.length > 0) {
      return [...data.connections].sort((a, b) => b.strength - a.strength);
    }
    // Fallback: aggregate cross-district relationships (with shared terms) into
    // per-district-pair connections.
    const agg = new Map<
      string,
      {
        sourceDistrictId: number;
        targetDistrictId: number;
        strength: number;
        terms: Set<string>;
        // The rationale from the strongest bridging relationship, if any, so the
        // legend can still surface a sentence in the degraded path.
        rationale?: string;
        rationaleWeight: number;
      }
    >();
    data.relationships.forEach((r) => {
      const terms = r.sharedTerms ?? [];
      if (terms.length === 0) return;
      const s = docById.get(r.source);
      const t = docById.get(r.target);
      if (!s || !t || s.districtId === t.districtId) return;
      const a = Math.min(s.districtId, t.districtId);
      const b = Math.max(s.districtId, t.districtId);
      const key = `${a}-${b}`;
      const entry =
        agg.get(key) ??
        {
          sourceDistrictId: a,
          targetDistrictId: b,
          strength: 0,
          terms: new Set<string>(),
          rationaleWeight: -1,
        };
      entry.strength += r.weight || 1;
      terms.forEach((term) => entry.terms.add(term));
      if (r.rationale && (r.weight || 0) > entry.rationaleWeight) {
        entry.rationale = r.rationale;
        entry.rationaleWeight = r.weight || 0;
      }
      agg.set(key, entry);
    });
    return [...agg.values()]
      .map((e) => ({
        sourceDistrictId: e.sourceDistrictId,
        targetDistrictId: e.targetDistrictId,
        strength: e.strength,
        sharedTerms: [...e.terms],
        rationale: e.rationale,
      }))
      .sort((a, b) => b.strength - a.strength);
  }, [data, docById]);

  // Legend rows: top cross-district connections, resolved to district labels.
  const legendConnections = useMemo<LegendConnection[]>(() => {
    return connections
      .map((c) => {
        const src = districtById.get(c.sourceDistrictId);
        const tgt = districtById.get(c.targetDistrictId);
        if (!src || !tgt) return null;
        return {
          key: `${c.sourceDistrictId}-${c.targetDistrictId}`,
          sourceLabel: src.label,
          targetLabel: tgt.label,
          sharedTerms: c.sharedTerms,
          rationale: c.rationale,
          sourceDistrictId: c.sourceDistrictId,
          targetDistrictId: c.targetDistrictId,
        } as LegendConnection;
      })
      .filter((x): x is LegendConnection => x !== null);
  }, [connections, districtById]);

  // The "focused" doc is the selection, else the hover.
  const focusId = selectedId ?? hoveredId;

  // Documents illuminated when something is focused (focus + its neighbors),
  // OR when a district pair is spotlighted (the docs bridging the two districts).
  const illuminated = useMemo(() => {
    if (hoveredPair) {
      const set = new Set<number>();
      data?.relationships.forEach((r) => {
        const s = docById.get(r.source);
        const t = docById.get(r.target);
        if (!s || !t) return;
        const ids = new Set([s.districtId, t.districtId]);
        if (
          ids.has(hoveredPair.sourceDistrictId) &&
          ids.has(hoveredPair.targetDistrictId)
        ) {
          set.add(r.source);
          set.add(r.target);
        }
      });
      if (set.size > 0) return set;
    }
    // A specific node (hover or selection) takes precedence over a district.
    if (focusId !== null) {
      const set = new Set<number>([focusId]);
      neighbors.get(focusId)?.forEach((id) => set.add(id));
      return set;
    }
    if (hoveredDistrictId !== null) {
      // Spotlight every document on a CROSS-district link touching this district.
      const set = new Set<number>();
      data?.relationships.forEach((r) => {
        const s = docById.get(r.source);
        const t = docById.get(r.target);
        if (!s || !t || s.districtId === t.districtId) return;
        if (s.districtId === hoveredDistrictId || t.districtId === hoveredDistrictId) {
          set.add(r.source);
          set.add(r.target);
        }
      });
      if (set.size > 0) return set;
    }
    return null;
  }, [focusId, neighbors, hoveredPair, hoveredDistrictId, data, docById]);

  // When a search is active, the matched documents become the "illuminated" set
  // so connectors/dots/labels for the matched landmarks light and everything
  // else dims — exactly the focus treatment, but driven by the answer set. A
  // live hover/selection (focusId) still takes precedence over the search frame.
  const effectiveIlluminated = useMemo(() => {
    if (focusId === null && isSearchActive && highlightedIds.size > 0) {
      return highlightedIds;
    }
    return illuminated;
  }, [illuminated, isSearchActive, highlightedIds, focusId]);

  // Hover chip for a hovered district. Prefer the backend's written `summary`
  // sentence (clearer); otherwise fall back to the cross-district bridge themes.
  const districtBridge = useMemo(() => {
    if (hoveredDistrictId === null) return null;
    const d = districtById.get(hoveredDistrictId);
    if (!d) return null;
    const summary = d.summary?.trim() || null;
    let themes = d.bridgeThemes ?? [];
    if (themes.length === 0) {
      // Degrade: gather shared terms from this district's cross-district connections.
      const terms = new Set<string>();
      connections.forEach((c) => {
        if (
          c.sourceDistrictId === hoveredDistrictId ||
          c.targetDistrictId === hoveredDistrictId
        ) {
          c.sharedTerms.forEach((t) => terms.add(t));
        }
      });
      themes = [...terms];
    }
    // Nothing to show at all -> no chip.
    if (!summary && themes.length === 0) return null;
    return { label: d.label, summary, themes: themes.slice(0, 6) };
  }, [hoveredDistrictId, districtById, connections]);

  // Hover "why" card: the hovered landmark's connected documents WITH the shared
  // themes for each, so the user reads WHY without clicking. Only on hover (not
  // a sticky selection) and only when we actually have a cursor position.
  const hoverCard = useMemo(() => {
    if (hoveredId === null || cursor === null) return null;
    const doc = docById.get(hoveredId);
    if (!doc) return null;
    const rels = relsByDoc.get(hoveredId) ?? [];
    const conns: HoverConnection[] = rels
      .map((r) => {
        const otherId = r.source === hoveredId ? r.target : r.source;
        const other = docById.get(otherId);
        if (!other) return null;
        return {
          id: other.id,
          title: other.title,
          sharedTerms: r.sharedTerms ?? [],
          rationale: r.rationale,
          weight: r.weight || 0,
        } as HoverConnection;
      })
      .filter((x): x is HoverConnection => x !== null)
      .sort((a, b) => b.weight - a.weight);
    const district = districtById.get(doc.districtId);
    return {
      title: doc.title,
      subtitle: district?.label ?? doc.docType ?? null,
      connections: conns,
      x: cursor.x,
      y: cursor.y,
    };
  }, [hoveredId, cursor, docById, relsByDoc, districtById]);

  // Top cross-district edges to label on the map with their shared theme. Picks
  // the strongest connections, finds a representative document pair bridging the
  // two districts, and labels that edge's midpoint with the top shared term.
  const edgeLabels = useMemo(() => {
    if (!data) return [];
    const out: { id: string; x: number; y: number; text: string }[] = [];
    for (const c of connections) {
      if (out.length >= VIS.edgeLabelMax) break;
      const term = c.sharedTerms[0];
      if (!term) continue;
      // Find the strongest relationship that actually bridges this district pair.
      let best: AtlasRelationship | null = null;
      for (const r of data.relationships) {
        const s = docById.get(r.source);
        const t = docById.get(r.target);
        if (!s || !t) continue;
        const ids = new Set([s.districtId, t.districtId]);
        if (ids.has(c.sourceDistrictId) && ids.has(c.targetDistrictId)) {
          if (!best || (r.weight || 0) > (best.weight || 0)) best = r;
        }
      }
      if (!best) continue;
      const s = docById.get(best.source)!;
      const t = docById.get(best.target)!;
      out.push({
        id: `${c.sourceDistrictId}-${c.targetDistrictId}`,
        x: (s.x + t.x) / 2,
        y: (s.y + t.y) / 2,
        text: term,
      });
    }
    return out;
  }, [data, connections, docById]);

  // --------------------------------------------------------------------------
  // Knowledge gaps (coverage panel + on-map flags)
  // --------------------------------------------------------------------------
  const gaps = useMemo<AtlasGap[]>(() => data?.gaps ?? [], [data]);

  // Isolated documents get a distinct hollow muted ring on the map. We flag any
  // gap of kind 'isolated' (or 'underconnected') that points at a document.
  const isolatedDocs = useMemo(() => {
    const out: AtlasDocument[] = [];
    const seen = new Set<number>();
    for (const g of gaps) {
      if (g.kind !== 'isolated') continue;
      if (g.documentId == null || seen.has(g.documentId)) continue;
      const doc = docById.get(g.documentId);
      if (doc) {
        out.push(doc);
        seen.add(g.documentId);
      }
    }
    return out;
  }, [gaps, docById]);

  // Thin districts get a faint hatched overlay. Only those with a renderable
  // boundary polygon (otherwise nothing subtle to hatch over).
  const thinDistricts = useMemo(() => {
    const out: AtlasDistrict[] = [];
    const seen = new Set<number>();
    for (const g of gaps) {
      if (g.kind !== 'thin') continue;
      if (g.districtId == null || seen.has(g.districtId)) continue;
      const d = districtById.get(g.districtId);
      if (d && Array.isArray(d.boundary) && d.boundary.length >= 3) {
        out.push(d);
        seen.add(g.districtId);
      }
    }
    return out;
  }, [gaps, districtById]);

  // --------------------------------------------------------------------------
  // Camera helpers
  // --------------------------------------------------------------------------
  const flyTo = useCallback((x: number, y: number, targetZoom = 1.1) => {
    setViewState((vs) => ({
      ...vs,
      target: [x, y, 0],
      zoom: targetZoom,
      ...CAMERA_TRANSITION,
    }));
  }, []);

  const flyToDocument = useCallback(
    (id: number) => {
      const doc = docById.get(id);
      if (!doc) return;
      setSelectedId(id);
      flyTo(doc.x, doc.y, 1.2);
    },
    [docById, flyTo]
  );

  const closePanel = useCallback(() => {
    setSelectedId(null);
    setHighlightedIds(new Set());
    if (data) fitToContent(data, setViewState);
  }, [data]);

  // --------------------------------------------------------------------------
  // Search
  // --------------------------------------------------------------------------
  const runSearch = useCallback(
    (q: string) => {
      if (municipalityId === null) return;
      // A search reframes the map around the answer set, so clear any sticky
      // selection / district hover that would otherwise fight the dim treatment.
      setSelectedId(null);
      setHoveredPair(null);
      setSearching(true);
      searchAtlas(municipalityId, q)
        .then((results) => {
          setSearchResults(results);
          const docIds = new Set(results.documents.map((d) => d.id));
          const districtIds = new Set(results.districts.map((d) => d.id));
          setHighlightedIds(docIds);
          setMatchedDistrictIds(districtIds);
          // FLY to FRAME the whole matched set (its bounding box), not just the
          // top hit, so the user sees the answer in context. Falls back to the
          // top document, then the matched districts' centers.
          const pts: { x: number; y: number }[] = [];
          results.documents.forEach((d) => {
            const doc = docById.get(d.id);
            if (doc) pts.push({ x: doc.x, y: doc.y });
          });
          results.districts.forEach((d) => {
            const dist = districtById.get(d.id);
            if (dist) pts.push({ x: dist.centerX, y: dist.centerY });
          });
          if (pts.length > 0) fitToPoints(pts, setViewState);
        })
        .catch(() => {
          setSearchResults({ documents: [], districts: [] });
          setHighlightedIds(new Set());
          setMatchedDistrictIds(new Set());
        })
        .finally(() => setSearching(false));
    },
    [municipalityId, docById, districtById]
  );

  // Fly to + spotlight a whole district zone (from a search result row).
  const flyToDistrict = useCallback(
    (id: number) => {
      const d = districtById.get(id);
      if (!d) return;
      setHoveredDistrictId(id);
      flyTo(d.centerX, d.centerY, 0.2);
    },
    [districtById, flyTo]
  );

  const clearSearch = useCallback(() => {
    setSearchResults(null);
    setHighlightedIds(new Set());
    setMatchedDistrictIds(new Set());
    // Restore the normal framing of the whole atlas.
    if (data) fitToContent(data, setViewState);
  }, [data]);

  // --------------------------------------------------------------------------
  // Layers — draw order (bottom -> top):
  //   zone shadow -> zone fill -> zone border -> connectors ->
  //   landmark nodes -> downtown core -> district labels -> core label ->
  //   landmark labels
  // (The light paper + graph-paper grid live in CSS behind a transparent canvas.)
  // --------------------------------------------------------------------------
  const lod = lodFromZoom(zoom);

  // A viewport mirroring the controlled viewState + live canvas size, so the
  // HTML document-card overlay can project() world coords to canvas px. (deck's
  // own internal viewport is not exposed cleanly from a controlled viewState,
  // so we reconstruct it from the same inputs — identical math.)
  const cardViewport = useMemo(() => {
    if (canvasSize.width === 0 || canvasSize.height === 0) return null;
    return new OrthographicViewport({
      id: VIEW_ID,
      width: canvasSize.width,
      height: canvasSize.height,
      target: (viewState.target as [number, number, number]) ?? [0, 0, 0],
      zoom: (viewState.zoom as number) ?? -1.2,
    });
  }, [canvasSize.width, canvasSize.height, viewState.target, viewState.zoom]);

  // Cross-fade: as cards fade IN, the dots/labels fade OUT across the same band
  // so the swap reads as a single smooth transition rather than two events.
  const dotFade =
    1 - smoothstep(VIS.card.dotFadeOutStart, VIS.card.dotFadeOutEnd, zoom);

  const layers = useMemo(() => {
    if (!data) return [];
    const built: unknown[] = [];
    const capitalId = capital?.id ?? null;

    const withBoundary = data.districts.filter(
      (d) => Array.isArray(d.boundary) && d.boundary.length >= 3
    );
    const withoutBoundary = data.districts.filter(
      (d) => !Array.isArray(d.boundary) || d.boundary.length < 3
    );
    const districtAlpha = lod.districts * reveal;

    // ----- (1) Zone elevation shadow: offset + slightly larger polygon -----
    if (withBoundary.length > 0) {
      built.push(
        new PolygonLayer<AtlasDistrict>({
          id: 'zone-shadow',
          data: withBoundary,
          getPolygon: (d) =>
            offsetRing(
              scaleRing(d.boundary!, d.centerX, d.centerY, VIS.zoneShadowScale),
              VIS.zoneShadowOffset
            ),
          getFillColor: () =>
            [20, 26, 33, Math.round(VIS.zoneShadowAlpha * 255 * districtAlpha)] as RGBA,
          stroked: false,
          filled: true,
          updateTriggers: { getFillColor: [districtAlpha] },
        })
      );
    }

    // ----- (2) Zone fill: pale zoning tint (gold for search-matched zones) -----
    if (withBoundary.length > 0) {
      built.push(
        new PolygonLayer<AtlasDistrict>({
          id: 'zone-fill',
          data: withBoundary,
          pickable: true,
          getPolygon: (d) => d.boundary!,
          getFillColor: (d) => {
            const matched = matchedDistrictIds.has(d.id);
            // On a search, matched zones take a subtle civic-gold tint; the rest
            // recede so the answer set stands out.
            if (matched) {
              const g = VIS.search.matchedDistrictTint;
              return [
                g[0],
                g[1],
                g[2],
                Math.round(VIS.search.matchedDistrictFillAlpha * 255 * districtAlpha),
              ] as RGBA;
            }
            const c = muted(hexToRgb(d.color));
            const dim = isSearchActive ? VIS.search.dimZoneFillMul : 1;
            return [c[0], c[1], c[2], Math.round(VIS.zoneFillAlpha * 255 * districtAlpha * dim)] as RGBA;
          },
          stroked: false,
          filled: true,
          onHover: (info: PickingInfo<AtlasDistrict>) =>
            setHoveredDistrictId(info.object ? info.object.id : null),
          updateTriggers: { getFillColor: [districtAlpha, matchedDistrictIds, isSearchActive] },
        })
      );
    }

    // ----- (3) Zone border: crisp parcel edge (gold for search-matched zones) -----
    if (withBoundary.length > 0) {
      built.push(
        new PolygonLayer<AtlasDistrict>({
          id: 'zone-border',
          data: withBoundary,
          getPolygon: (d) => d.boundary!,
          filled: false,
          stroked: true,
          getLineColor: (d) => {
            const matched = matchedDistrictIds.has(d.id);
            if (matched) {
              const g = darken([...VIS.search.matchedDistrictTint], 0.05);
              return [g[0], g[1], g[2], Math.round(VIS.search.matchedDistrictBorderAlpha * districtAlpha)] as RGBA;
            }
            const c = darken(muted(hexToRgb(d.color)), VIS.zoneBorderDarken);
            const dim = isSearchActive ? VIS.search.dimZoneBorderMul : 1;
            return [c[0], c[1], c[2], Math.round(VIS.zoneBorderAlpha * districtAlpha * dim)] as RGBA;
          },
          getLineWidth: (d) =>
            matchedDistrictIds.has(d.id)
              ? VIS.search.matchedDistrictBorderWidthPx
              : VIS.zoneBorderWidthPx,
          lineWidthUnits: 'pixels',
          updateTriggers: {
            getLineColor: [districtAlpha, matchedDistrictIds, isSearchActive],
            getLineWidth: [matchedDistrictIds],
          },
        })
      );
    }

    // ----- (3a) Thin-district coverage flag: faint hatched overlay + tag -----
    // Subtle "shallow coverage" indicator over districts the backend flagged as
    // thin. A faint diagonal-leaning warm tint reads as a hatch without a custom
    // shader; suppressed during a search so the answer treatment stays clean.
    if (thinDistricts.length > 0 && !isSearchActive) {
      built.push(
        new PolygonLayer<AtlasDistrict>({
          id: 'thin-district-hatch',
          data: thinDistricts,
          getPolygon: (d) => d.boundary!,
          stroked: false,
          filled: true,
          getFillColor: () => {
            const c = VIS.gap.thinHatchColor;
            return [c[0], c[1], c[2], Math.round(VIS.gap.thinHatchAlpha * 255 * districtAlpha)] as RGBA;
          },
          updateTriggers: { getFillColor: [districtAlpha] },
        })
      );
      // A small "shallow coverage" tag near the district center.
      built.push(
        new TextLayer<AtlasDistrict>({
          id: 'thin-district-tag',
          data: thinDistricts,
          getPosition: (d) => [d.centerX, d.centerY],
          getText: () => 'shallow coverage',
          getSize: 9,
          sizeUnits: 'pixels',
          getColor: [120, 112, 96, Math.round(235 * districtAlpha)],
          getTextAnchor: 'middle',
          getAlignmentBaseline: 'center',
          fontFamily: VIS.font,
          fontWeight: 500,
          characterSet: 'auto',
          getPixelOffset: [0, 24],
          background: true,
          getBackgroundColor: [244, 246, 248, Math.round(190 * districtAlpha)],
          backgroundPadding: [4, 2],
          outlineWidth: 2,
          outlineColor: [...VIS.labelPaperOutline, 200],
          fontSettings: { sdf: true, radius: 12 },
          updateTriggers: { getColor: [districtAlpha], getBackgroundColor: [districtAlpha] },
        })
      );
    }

    // 3b. Fallback faint disc for boundary-less districts (graceful degrade).
    withoutBoundary.forEach((district) => {
      const c = muted(hexToRgb(district.color));
      built.push(
        new ScatterplotLayer<{ d: AtlasDistrict }>({
          id: `zone-fallback-${district.id}`,
          data: [{ d: district }],
          getPosition: () => [district.centerX, district.centerY],
          getRadius: () => district.radius,
          radiusUnits: 'common',
          getFillColor: () =>
            [c[0], c[1], c[2], Math.round(VIS.zoneFillAlpha * 255 * districtAlpha)] as RGBA,
          stroked: true,
          getLineColor: () => {
            const b = darken(c, VIS.zoneBorderDarken);
            return [b[0], b[1], b[2], Math.round(VIS.zoneBorderAlpha * districtAlpha)] as RGBA;
          },
          getLineWidth: VIS.zoneBorderWidthPx,
          lineWidthUnits: 'pixels',
          updateTriggers: { getFillColor: [districtAlpha], getLineColor: [districtAlpha] },
        })
      );
    });

    // ----- (4) Connector network: relationships as quiet streets -----
    if (lod.roads > 0.01) {
      built.push(
        new LineLayer<{ source: number; target: number; weight: number }>({
          id: 'connectors',
          data: data.relationships,
          getSourcePosition: (r) => {
            const s = docById.get(r.source);
            return s ? [s.x, s.y] : [0, 0];
          },
          getTargetPosition: (r) => {
            const t = docById.get(r.target);
            return t ? [t.x, t.y] : [0, 0];
          },
          getColor: (r) => connectorColor(r, effectiveIlluminated, lod.roads * reveal),
          getWidth: (r) => {
            const lit =
              effectiveIlluminated &&
              (effectiveIlluminated.has(r.source) || effectiveIlluminated.has(r.target));
            return lit ? VIS.connectorWidthLit : VIS.connectorWidthDefault;
          },
          widthUnits: 'pixels',
          updateTriggers: {
            getColor: [effectiveIlluminated, lod.roads, reveal],
            getWidth: [effectiveIlluminated],
          },
        })
      );
    }

    // ----- (5) Landmarks: documents as small crisp map nodes -----
    // The dots fade OUT (dotFade) as the document info cards fade IN, so the
    // close-zoom swap reads as a single smooth cross-fade.
    if (lod.landmarks * dotFade > 0.01) {
      const dotsLod = lod.landmarks * dotFade;
      built.push(
        new ScatterplotLayer<AtlasDocument>({
          id: 'landmark-nodes',
          data: data.documents,
          pickable: true,
          getPosition: (d) => [d.x, d.y],
          getRadius: (d) => landmarkRadius(d),
          radiusUnits: 'common',
          radiusMinPixels: 1.5,
          getFillColor: (d) =>
            d.id === capitalId
              ? [0, 0, 0, 0]
              : landmarkColor(d, effectiveIlluminated, highlightedIds, dotsLod * reveal),
          stroked: true,
          getLineColor: (d) => {
            if (d.id === capitalId) return [0, 0, 0, 0];
            const base = highlightedIds.has(d.id)
              ? VIS.landmarkSearchHit
              : effectiveIlluminated && effectiveIlluminated.has(d.id)
                ? VIS.landmarkLit
                : VIS.landmarkFill;
            const c = darken([...base], VIS.landmarkBorderDarken);
            const dim = effectiveIlluminated && !effectiveIlluminated.has(d.id) ? 0.4 : 1;
            return [c[0], c[1], c[2], Math.round(235 * dotsLod * reveal * dim)] as RGBA;
          },
          lineWidthMinPixels: VIS.landmarkBorderWidthPx,
          onHover: (info: PickingInfo<AtlasDocument>) =>
            setHoveredId(info.object ? info.object.id : null),
          onClick: (info: PickingInfo<AtlasDocument>) => {
            if (info.object) flyToDocument(info.object.id);
          },
          updateTriggers: {
            getFillColor: [effectiveIlluminated, highlightedIds, dotsLod, reveal, capitalId],
            getLineColor: [effectiveIlluminated, highlightedIds, dotsLod, reveal, capitalId],
          },
        })
      );
    }

    // ----- (5a) Isolated-document flags: hollow muted rings -----
    // Disconnected knowledge gets a small HOLLOW ring (no fill) so it's
    // spottable as "open / unwired" at a glance, without alarm. Fades with the
    // landmark dots and is suppressed during a search (the gold treatment owns
    // emphasis then).
    if (isolatedDocs.length > 0 && lod.landmarks * dotFade > 0.01 && !isSearchActive) {
      const flagLod = lod.landmarks * dotFade * reveal;
      built.push(
        new ScatterplotLayer<AtlasDocument>({
          id: 'isolated-flags',
          data: isolatedDocs,
          getPosition: (d) => [d.x, d.y],
          getRadius: (d) => landmarkRadius(d) * VIS.gap.isolatedRingRadiusMul,
          radiusUnits: 'common',
          radiusMinPixels: 4,
          filled: false,
          stroked: true,
          getLineColor: () => {
            const c = VIS.gap.isolatedRingColor;
            return [c[0], c[1], c[2], Math.round(VIS.gap.isolatedRingAlpha * flagLod)] as RGBA;
          },
          getLineWidth: VIS.gap.isolatedRingWidthPx,
          lineWidthUnits: 'pixels',
          updateTriggers: { getLineColor: [flagLod] },
        })
      );
    }

    // ----- (6) Downtown core: the civic anchor, always visible -----
    // Like the landmark dots, the core marker/ring fade with dotFade so the
    // prominent downtown-core CARD takes over cleanly at close zoom.
    if (capital) {
      const baseR = landmarkRadius(capital);
      const coreLod = lod.capital * dotFade;
      // 6a. Concentric "city-center" ring (thin stroked circle outside the marker).
      built.push(
        new ScatterplotLayer<AtlasDocument>({
          id: 'core-ring',
          data: [capital],
          getPosition: (d) => [d.x, d.y],
          getRadius: () => baseR * VIS.coreRingRadiusMul,
          radiusUnits: 'common',
          filled: false,
          stroked: true,
          getLineColor: () =>
            [VIS.coreRing[0], VIS.coreRing[1], VIS.coreRing[2], Math.round(VIS.coreRingAlpha * coreLod * reveal)] as RGBA,
          getLineWidth: VIS.coreRingWidthPx,
          lineWidthUnits: 'pixels',
          updateTriggers: { getLineColor: [coreLod, reveal] },
        })
      );
      // 6b. Bold civic marker (pickable, so the core flies-in like any landmark).
      built.push(
        new ScatterplotLayer<AtlasDocument>({
          id: 'core-marker',
          data: [capital],
          pickable: true,
          getPosition: (d) => [d.x, d.y],
          getRadius: () => baseR * VIS.coreMarkerRadiusMul,
          radiusUnits: 'common',
          radiusMinPixels: 4,
          getFillColor: () =>
            [VIS.coreAccent[0], VIS.coreAccent[1], VIS.coreAccent[2], Math.round(255 * coreLod * reveal)] as RGBA,
          stroked: true,
          getLineColor: () =>
            [VIS.coreBorderColor[0], VIS.coreBorderColor[1], VIS.coreBorderColor[2], Math.round(255 * coreLod * reveal)] as RGBA,
          lineWidthMinPixels: VIS.coreBorderWidthPx,
          onHover: (info: PickingInfo<AtlasDocument>) =>
            setHoveredId(info.object ? info.object.id : null),
          onClick: (info: PickingInfo<AtlasDocument>) => {
            if (info.object) flyToDocument(info.object.id);
          },
          updateTriggers: { getFillColor: [coreLod, reveal], getLineColor: [coreLod, reveal] },
        })
      );
    }

    // ----- (7) District labels: confident dark slate signposts -----
    built.push(
      new TextLayer<AtlasDistrict>({
        id: 'district-labels',
        data: data.districts,
        getPosition: (d) => [d.centerX, d.centerY],
        getText: (d) => d.label.toUpperCase(),
        getSize: VIS.districtLabelSize,
        sizeUnits: 'pixels',
        getColor: [
          VIS.districtLabelColor[0],
          VIS.districtLabelColor[1],
          VIS.districtLabelColor[2],
          Math.round(255 * districtAlpha),
        ],
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'center',
        fontFamily: VIS.font,
        fontWeight: 600,
        characterSet: 'auto',
        getPixelOffset: [0, -9],
        background: true,
        getBackgroundColor: [
          VIS.districtLabelBg[0],
          VIS.districtLabelBg[1],
          VIS.districtLabelBg[2],
          Math.round(190 * districtAlpha),
        ],
        backgroundPadding: [6, 3],
        outlineWidth: 2,
        outlineColor: [...VIS.labelPaperOutline, 220],
        fontSettings: { sdf: true, radius: 12 },
        updateTriggers: { getColor: [districtAlpha], getBackgroundColor: [districtAlpha] },
      })
    );
    // District counts beneath labels (quieter grey subtitle).
    built.push(
      new TextLayer<AtlasDistrict>({
        id: 'district-counts',
        data: data.districts,
        getPosition: (d) => [d.centerX, d.centerY],
        getText: (d) => `${d.documentCount} documents`,
        getSize: VIS.districtCountSize,
        sizeUnits: 'pixels',
        getColor: [
          VIS.districtCountColor[0],
          VIS.districtCountColor[1],
          VIS.districtCountColor[2],
          Math.round(235 * districtAlpha),
        ],
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'center',
        fontFamily: VIS.font,
        getPixelOffset: [0, 11],
        outlineWidth: 2,
        outlineColor: [...VIS.labelPaperOutline, 220],
        fontSettings: { sdf: true, radius: 12 },
        updateTriggers: { getColor: [districtAlpha] },
      })
    );

    // ----- (8) Downtown core label: visible until the core card takes over -----
    // Fades with dotFade so it doesn't double up with the prominent core card.
    if (capital) {
      const coreLabelLod = lod.capital * dotFade;
      built.push(
        new TextLayer<AtlasDocument>({
          id: 'core-label',
          data: [capital],
          getPosition: (d) => [d.x, d.y],
          getText: (d) => shortenCoreLabel(d.title),
          getSize: VIS.coreLabelSize,
          sizeUnits: 'pixels',
          getColor: [
            VIS.coreLabelColor[0],
            VIS.coreLabelColor[1],
            VIS.coreLabelColor[2],
            Math.round(255 * coreLabelLod * reveal),
          ],
          getTextAnchor: 'start',
          getAlignmentBaseline: 'center',
          fontFamily: VIS.font,
          fontWeight: 700,
          characterSet: 'auto',
          getPixelOffset: [16, 0],
          background: true,
          getBackgroundColor: [
            VIS.districtLabelBg[0],
            VIS.districtLabelBg[1],
            VIS.districtLabelBg[2],
            Math.round(210 * coreLabelLod * reveal),
          ],
          backgroundPadding: [6, 4],
          outlineWidth: 2,
          outlineColor: [...VIS.labelPaperOutline, 220],
          fontSettings: { sdf: true, radius: 12 },
          updateTriggers: { getColor: [coreLabelLod, reveal], getBackgroundColor: [coreLabelLod, reveal] },
        })
      );
    }

    // ----- (9) Landmark titles (zoomed in; core excluded, it has its own) -----
    // Also fade with dotFade: once cards take over (which carry the title) the
    // free-floating labels recede so the two never double up.
    if (lod.landmarkLabels * dotFade > 0.01) {
      const labelsLod = lod.landmarkLabels * dotFade;
      built.push(
        new TextLayer<AtlasDocument>({
          id: 'landmark-labels',
          data: capitalId === null ? data.documents : data.documents.filter((d) => d.id !== capitalId),
          getPosition: (d) => [d.x, d.y],
          getText: (d) => d.title,
          getSize: VIS.landmarkLabelSize,
          sizeUnits: 'pixels',
          getColor: (d) => {
            const dim = effectiveIlluminated && !effectiveIlluminated.has(d.id) ? 0.3 : 1;
            return [
              VIS.landmarkLabelColor[0],
              VIS.landmarkLabelColor[1],
              VIS.landmarkLabelColor[2],
              Math.round(235 * labelsLod * reveal * dim),
            ] as RGBA;
          },
          getTextAnchor: 'start',
          getAlignmentBaseline: 'center',
          fontFamily: VIS.font,
          getPixelOffset: [10, 0],
          characterSet: 'auto',
          outlineWidth: 2,
          outlineColor: [...VIS.labelPaperOutline, 220],
          fontSettings: { sdf: true, radius: 12 },
          updateTriggers: { getColor: [effectiveIlluminated, labelsLod, reveal] },
        })
      );
    }

    // ----- (10) Cross-district edge theme labels: the map narrates itself -----
    // Only the top few most-significant edges, fading with zoom (lod.edgeLabels)
    // so they never clutter when zoomed all the way in or out.
    if (lod.edgeLabels > 0.01 && edgeLabels.length > 0) {
      const ea = lod.edgeLabels * reveal;
      built.push(
        new TextLayer<{ id: string; x: number; y: number; text: string }>({
          id: 'edge-theme-labels',
          data: edgeLabels,
          getPosition: (e) => [e.x, e.y],
          getText: (e) => e.text,
          getSize: VIS.edgeLabelSize,
          sizeUnits: 'pixels',
          getColor: [
            VIS.edgeLabelColor[0],
            VIS.edgeLabelColor[1],
            VIS.edgeLabelColor[2],
            Math.round(VIS.edgeLabelAlpha * ea),
          ],
          getTextAnchor: 'middle',
          getAlignmentBaseline: 'center',
          fontFamily: VIS.font,
          fontWeight: 500,
          characterSet: 'auto',
          background: true,
          getBackgroundColor: [
            VIS.edgeLabelBg[0],
            VIS.edgeLabelBg[1],
            VIS.edgeLabelBg[2],
            Math.round(VIS.edgeLabelBgAlpha * ea),
          ],
          backgroundPadding: [5, 2],
          outlineWidth: 2,
          outlineColor: [...VIS.labelPaperOutline, 200],
          fontSettings: { sdf: true, radius: 12 },
          updateTriggers: { getColor: [ea], getBackgroundColor: [ea] },
        })
      );
    }

    return built;
  }, [
    data,
    capital,
    docById,
    illuminated,
    effectiveIlluminated,
    highlightedIds,
    matchedDistrictIds,
    isSearchActive,
    isolatedDocs,
    thinDistricts,
    lod.districts,
    lod.roads,
    lod.landmarks,
    lod.landmarkLabels,
    lod.capital,
    lod.edgeLabels,
    dotFade,
    edgeLabels,
    reveal,
    flyToDocument,
  ]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  const selectedMunicipality = municipalities.find((m) => m.id === municipalityId) ?? null;
  const isEmpty =
    !loading && !error && data !== null && data.documents.length === 0;
  const missingEmbeddings = data?.meta.documentsWithoutEmbeddings ?? 0;

  // Blueprint / Figma graph-paper grid, painted as a CSS background behind the
  // (transparent) deck canvas. Fine cells + stronger major rules.
  const gridBackground = useMemo(
    () => ({
      backgroundColor: VIS.canvasBase,
      backgroundImage: [
        `linear-gradient(to right, ${VIS.gridMajor} 1px, transparent 1px)`,
        `linear-gradient(to bottom, ${VIS.gridMajor} 1px, transparent 1px)`,
        `linear-gradient(to right, ${VIS.gridFine} 1px, transparent 1px)`,
        `linear-gradient(to bottom, ${VIS.gridFine} 1px, transparent 1px)`,
      ].join(','),
      backgroundSize: [
        `${VIS.gridMajorStep}px ${VIS.gridMajorStep}px`,
        `${VIS.gridMajorStep}px ${VIS.gridMajorStep}px`,
        `${VIS.gridFineStep}px ${VIS.gridFineStep}px`,
        `${VIS.gridFineStep}px ${VIS.gridFineStep}px`,
      ].join(','),
    }),
    []
  );

  return (
    <>
      <Head title="Municipal Knowledge Atlas" />
      <div className="relative flex h-screen w-screen flex-col overflow-hidden bg-[#F4F6F8] text-slate-800">
        {/* Header (Linear-style light top bar) */}
        <header className="z-30 flex shrink-0 items-center gap-4 border-b border-slate-200 bg-white/90 px-5 py-3 backdrop-blur">
          <div className="flex items-center gap-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-slate-50">
              <OrdinalIcon className="h-4 w-4 text-slate-600" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-tight text-slate-900">
                Municipal Knowledge Atlas
              </div>
            </div>
          </div>

          <div className="ml-2">
            <Select
              value={municipalityId !== null ? String(municipalityId) : undefined}
              onValueChange={(v) => setMunicipalityId(Number(v))}
            >
              <SelectTrigger className="h-9 w-[220px] border-slate-200 bg-white text-slate-800">
                <SelectValue placeholder="Select a municipality" />
              </SelectTrigger>
              <SelectContent className="border-slate-200 bg-white text-slate-800">
                {municipalities.map((m) => (
                  // Unready cities are disabled (not hidden) so they read as "coming online", not
                  // missing. The background warm pass flips them ready, then they're selectable.
                  <SelectItem key={m.id} value={String(m.id)} disabled={!m.ready}>
                    {m.name}
                    <span className="ml-2 text-xs text-slate-400">
                      {m.ready ? m.documentCount : 'preparing…'}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="ml-auto flex items-center">
            <AtlasSearch
              onSearch={runSearch}
              searching={searching}
              results={searchResults}
              onClear={clearSearch}
              onSelectDocument={flyToDocument}
              onSelectDistrict={flyToDistrict}
            />
          </div>
        </header>

        {/* Canvas */}
        <div ref={canvasRef} className="relative flex-1">
          {/* Light planning paper + blueprint grid (behind the transparent deck canvas). */}
          <div className="pointer-events-none absolute inset-0 z-0" style={gridBackground} />

          {data && !isEmpty && (
            <DeckGL
              views={new OrthographicView({ id: VIEW_ID })}
              viewState={{ [VIEW_ID]: viewState } as any}
              controller={
                {
                  // Smooth, responsive scroll zoom centered on the cursor; gentle
                  // inertia for a fluid pan/zoom glide. See the ZOOM tunables.
                  scrollZoom: { speed: ZOOM.scrollSpeed, smooth: ZOOM.scrollSmooth },
                  inertia: ZOOM.inertiaMs,
                  doubleClickZoom: true,
                  dragPan: true,
                  touchZoom: true,
                } as any
              }
              onViewStateChange={(params: any) => {
                const next = params.viewState?.[VIEW_ID] ?? params.viewState;
                setViewState(next);
              }}
              layers={layers as any}
              onHover={(info: PickingInfo) => {
                // Track the cursor in VIEWPORT px so the (fixed-positioned) hover
                // "why" card floats near it. deck reports canvas-relative coords,
                // so add the canvas wrapper's offset. (Layer onHover handlers own
                // which node is hovered.)
                if (info.x !== undefined && info.y !== undefined) {
                  const rect = canvasRef.current?.getBoundingClientRect();
                  setCursor({
                    x: info.x + (rect?.left ?? 0),
                    y: info.y + (rect?.top ?? 0),
                  });
                }
              }}
              getCursor={({ isDragging }: { isDragging: boolean }) =>
                isDragging ? 'grabbing' : hoveredId ? 'pointer' : 'grab'
              }
              style={{ position: 'absolute', inset: '0', zIndex: '1' }}
            />
          )}

          {/* Semantic-zoom document info cards (HTML overlay, culled + capped).
              Cross-fades in as the dots fade out at close zoom. */}
          {data && !isEmpty && cardViewport && lod.cards > 0.01 && (
            <DocumentCards
              documents={data.documents}
              viewport={cardViewport}
              opacity={lod.cards * reveal}
              capitalId={capital?.id ?? null}
              illuminated={effectiveIlluminated}
              highlightedIds={highlightedIds}
              tunables={VIS.card}
              onHover={setHoveredId}
              onClick={flyToDocument}
            />
          )}

          {/* Loading state. A cold/evicted atlas warms in the background (data endpoint 202 +
              polling); show the one-time build copy so a multi-second wait reads as intentional. */}
          {loading && (
            <AtlasLoadingState
              label={building ? 'Building the knowledge map…' : 'Mapping districts…'}
              sublabel={
                building
                  ? `Charting ${selectedMunicipality?.name ?? 'this municipality'}'s knowledge. This runs once, then loads instantly for everyone.`
                  : undefined
              }
            />
          )}

          {/* Empty state */}
          {isEmpty && (
            <div className="absolute inset-0 z-20 flex items-center justify-center">
              <div className="max-w-sm text-center">
                <div className="text-sm font-medium text-slate-700">
                  No mapped knowledge yet
                </div>
                <p className="mt-2 text-sm text-slate-500">
                  {selectedMunicipality?.name ?? 'This municipality'} has no documents in the
                  atlas. Once documents are ingested and embedded, its knowledge map will appear
                  here.
                </p>
              </div>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="absolute inset-x-0 bottom-6 z-30 mx-auto w-fit rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 shadow-sm">
              {error}
            </div>
          )}

          {/* Coverage honesty footnote */}
          {missingEmbeddings > 0 && (
            <div className="absolute bottom-3 left-4 z-20 text-[11px] text-slate-400">
              {missingEmbeddings.toLocaleString()} document
              {missingEmbeddings === 1 ? '' : 's'} not yet embedded; coverage is partial.
            </div>
          )}

          {/* Quiet cross-district connections legend (bottom-left). */}
          {data && !isEmpty && legendConnections.length > 0 && (
            <ConnectionsLegend
              connections={legendConnections}
              onHoverPair={setHoveredPair}
            />
          )}

          {/* Coverage / knowledge-gap panel (bottom-right): what's MISSING. */}
          {data && !isEmpty && gaps.length > 0 && (
            <CoveragePanel
              gaps={gaps}
              onSelectDocument={flyToDocument}
              onSelectDistrict={flyToDistrict}
            />
          )}

          {/* Floating "why" card: reads connected docs + shared themes on hover. */}
          {hoverCard && (
            <HoverWhyCard
              title={hoverCard.title}
              subtitle={hoverCard.subtitle}
              connections={hoverCard.connections}
              x={hoverCard.x}
              y={hoverCard.y}
              viewportWidth={typeof window !== 'undefined' ? window.innerWidth : 1280}
              viewportHeight={typeof window !== 'undefined' ? window.innerHeight : 800}
            />
          )}

          {/* District bridge-themes chip (hovering a zone, no node under cursor). */}
          {districtBridge && hoveredId === null && cursor && (
            <div
              className="pointer-events-none fixed z-40 max-w-[260px] rounded-lg border border-slate-200 bg-white/95 px-3 py-2 backdrop-blur-sm"
              style={{
                left: Math.min(cursor.x + 16, (typeof window !== 'undefined' ? window.innerWidth : 1280) - 272),
                top: cursor.y + 14,
                boxShadow: '0 12px 32px -16px rgba(15,23,42,0.30)',
              }}
            >
              <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-400">
                {districtBridge.label}
                {!districtBridge.summary && ' · bridges via'}
              </div>
              {districtBridge.summary ? (
                // Prefer the written district description sentence.
                <div className="mt-1 text-[11px] leading-snug text-slate-600">
                  {districtBridge.summary}
                </div>
              ) : (
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {districtBridge.themes.map((t) => (
                    <span
                      key={t}
                      className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Intelligence panel */}
          {municipalityId !== null && (
            <IntelligencePanel
              documentId={selectedId}
              municipalityId={municipalityId}
              onClose={closePanel}
              onSelectDocument={flyToDocument}
            />
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Visual helpers (color/size per landmark + connector, polygon math, fit)
// ---------------------------------------------------------------------------

function landmarkRadius(d: AtlasDocument): number {
  // sqrt scaling so the most-connected landmarks read larger without dwarfing.
  const base = Math.sqrt(Math.max(1, d.degree || d.chunkCount || 1));
  return VIS.landmarkBaseRadius + base * VIS.landmarkDegreeScale;
}

function landmarkColor(
  d: AtlasDocument,
  illuminated: Set<number> | null,
  highlighted: Set<number>,
  lodLandmarks: number
): RGBA {
  const isHighlighted = highlighted.has(d.id);
  const dimmed = illuminated && !illuminated.has(d.id);
  const lit = illuminated && illuminated.has(d.id);

  let rgb: [number, number, number] = [...VIS.landmarkFill];
  if (isHighlighted) rgb = [...VIS.landmarkSearchHit];
  else if (lit) rgb = [...VIS.landmarkLit];

  let alpha: number = VIS.landmarkFillAlpha;
  if (dimmed) alpha = VIS.landmarkDimAlpha;
  alpha = alpha * lodLandmarks;

  return [rgb[0], rgb[1], rgb[2], Math.round(alpha)] as RGBA;
}

function connectorColor(
  r: { source: number; target: number },
  illuminated: Set<number> | null,
  lodRoads: number
): RGBA {
  const [lr, lg, lb] = VIS.connectorColor;
  const lit = illuminated && (illuminated.has(r.source) || illuminated.has(r.target));
  if (illuminated) {
    return lit
      ? [lr, lg, lb, Math.round(VIS.connectorAlphaLit * lodRoads)]
      : [lr, lg, lb, Math.round(VIS.connectorAlphaDimmed * lodRoads)];
  }
  return [lr, lg, lb, Math.round(VIS.connectorAlphaDefault * lodRoads)];
}

/** Scale a polygon ring outward from a center point (for the elevation shadow). */
function scaleRing(
  ring: [number, number][],
  cx: number,
  cy: number,
  scale: number
): [number, number][] {
  return ring.map(([x, y]) => [cx + (x - cx) * scale, cy + (y - cy) * scale]);
}

/** Offset a polygon ring by a world-unit delta (for the elevation shadow). */
function offsetRing(
  ring: [number, number][],
  [dx, dy]: [number, number]
): [number, number][] {
  return ring.map(([x, y]) => [x + dx, y + dy]);
}

/** Content bounding box over documents + district centers. */
function contentBounds(data: AtlasData): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const consider = (x: number, y: number) => {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  };
  data.documents.forEach((d) => consider(d.x, d.y));
  data.districts.forEach((d) => consider(d.centerX, d.centerY));
  if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  return { minX, minY, maxX, maxY };
}

/** Fit the camera to all content with an eased transition. */
function fitToContent(
  data: AtlasData,
  setViewState: React.Dispatch<React.SetStateAction<OrthographicViewState>>
) {
  const b = contentBounds(data);
  if (data.documents.length === 0 && data.districts.length === 0) return;

  const cx = (b.minX + b.maxX) / 2;
  const cy = (b.minY + b.maxY) / 2;
  const spanX = Math.max(1, b.maxX - b.minX);
  const spanY = Math.max(1, b.maxY - b.minY);
  const span = Math.max(spanX, spanY) * 1.25; // padding

  // OrthographicView: world units per pixel = 1 / 2^zoom. Pick a zoom that
  // fits `span` into ~900px of viewport.
  const targetPixels = 900;
  const zoom = clamp(Math.log2(targetPixels / span), ZOOM.minZoom, 1.0);

  setViewState((vs) => ({
    ...vs,
    target: [cx, cy, 0],
    zoom,
    ...CAMERA_TRANSITION,
  }));
}

/**
 * Fit + fly the camera to FRAME an arbitrary set of world points (the matched
 * set from "ask the atlas"). Same orthographic fit math as fitToContent, but
 * over the given points and capped so a single match doesn't punch in too far.
 */
function fitToPoints(
  pts: { x: number; y: number }[],
  setViewState: React.Dispatch<React.SetStateAction<OrthographicViewState>>
) {
  if (pts.length === 0) return;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of pts) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const spanX = Math.max(1, maxX - minX);
  const spanY = Math.max(1, maxY - minY);
  const span = Math.max(spanX, spanY) * VIS.search.fitPadding;
  const targetPixels = 900;
  const zoom = clamp(
    Math.log2(targetPixels / span),
    ZOOM.minZoom,
    VIS.search.fitMaxZoom
  );
  setViewState((vs) => ({
    ...vs,
    target: [cx, cy, 0],
    zoom,
    ...CAMERA_TRANSITION,
  }));
}

// ---------------------------------------------------------------------------
// Loading state ("mapping districts", a quiet planning-tool indicator)
// ---------------------------------------------------------------------------
function AtlasLoadingState({
  label = 'Mapping districts…',
  sublabel,
}: {
  label?: string;
  sublabel?: string;
}) {
  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center">
      <div className="flex max-w-xs flex-col items-center gap-5 text-center">
        <div className="flex items-center gap-1.5">
          <span className="h-2 w-2 animate-pulse rounded-full bg-slate-400 [animation-delay:0ms]" />
          <span className="h-2 w-2 animate-pulse rounded-full bg-slate-400 [animation-delay:150ms]" />
          <span className="h-2 w-2 animate-pulse rounded-full bg-slate-400 [animation-delay:300ms]" />
        </div>
        <div className="text-xs font-medium tracking-wide text-slate-500">{label}</div>
        {sublabel && <div className="text-[11px] leading-relaxed text-slate-400">{sublabel}</div>}
      </div>
    </div>
  );
}
